import { CartAddEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Holiday plan: the stitched hero and the planner (tree, mantel, gift table, plan tag).
 * Markup lives in sections/holiday-plan-hero.liquid and sections/holiday-planner.liquid.
 */

const NS = 'http://www.w3.org/2000/svg';
const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** @param {unknown} text */
const esc = (text) =>
  String(text ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] || c);

/**
 * Shopify CDN image at a given width.
 * @param {string} src
 * @param {number} width
 */
const sized = (src, width) => `${src}${src.includes('?') ? '&' : '?'}width=${width}`;

/**
 * @param {string} tag
 * @param {Record<string, string | number>} attrs
 * @param {Element} [parent]
 */
function svgEl(tag, attrs, parent) {
  const node = document.createElementNS(NS, tag);
  for (const key in attrs) node.setAttribute(key, String(attrs[key]));
  if (parent) parent.appendChild(node);
  return node;
}

/* ---------------------------------------------------------------- Hero */

/** @param {SVGSVGElement} svg */
function initHero(svg) {
  if (svg.dataset.hpReady) return;
  svg.dataset.hpReady = 'true';

  const tree = /** @type {SVGPathElement | null} */ (svg.querySelector('[data-hp-tree]'));
  const mask = svg.querySelector('[data-hp-mask]');
  const needle = svg.querySelector('[data-hp-needle]');
  if (!tree || !mask || !needle) return;

  if (reduceMotion()) {
    mask.setAttribute('stroke-dashoffset', '0');
    return;
  }

  const length = tree.getTotalLength();
  const duration = 2600;
  const start = performance.now() + 250;
  needle.setAttribute('opacity', '1');

  /** @param {number} now */
  const step = (now) => {
    const progress = Math.max(0, Math.min(1, (now - start) / duration));
    const eased = 1 - Math.pow(1 - progress, 2.2);
    mask.setAttribute('stroke-dashoffset', String(1 - eased));
    const a = tree.getPointAtLength(eased * length);
    const b = tree.getPointAtLength(Math.min(length, eased * length + 2));
    const angle = (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
    needle.setAttribute('transform', `translate(${a.x} ${a.y}) rotate(${angle})`);
    if (progress < 1) requestAnimationFrame(step);
    else needle.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 500, fill: 'forwards' });
  };
  requestAnimationFrame(step);
}

/* ------------------------------------------------------------- Planner */

/**
 * @typedef {{ handle: string, title: string, short: string, variant: string, price: number, swatch: string, cuff: string, thumb: string }} Fabric
 * @typedef {{ key: string, title: string, thumb: string, variant: string, price: number, qty: number, note: string }} PlanLine
 */

class HolidayPlanner {
  /** @param {HTMLElement} root */
  constructor(root) {
    this.root = root;
    this.storageKey = `hp-plan:${window.location.pathname}`;
    this.currency = root.dataset.currency || 'CAD';
    this.moneyFormat = root.dataset.moneyFormat || '${{amount}}';
    this.shipThreshold = Number(root.dataset.shipThreshold || 0);
    this.planTag = root.dataset.planTag || '';

    /** @type {HTMLElement[]} */
    this.shelves = [...root.querySelectorAll('[data-hp-shelf]')];
    /** @type {HTMLElement[]} */
    this.cards = [...root.querySelectorAll('[data-hp-card]')];

    this.mantel = /** @type {HTMLElement | null} */ (root.querySelector('[data-hp-mantel]'));
    /** @type {HTMLElement[]} */
    this.fabricRows = this.mantel ? [...this.mantel.querySelectorAll('[data-hp-fabric]')] : [];
    /** @type {Fabric[]} */
    this.fabrics = this.fabricRows.map((b) => ({
      handle: b.dataset.handle || '',
      title: b.dataset.title || '',
      short: b.dataset.short || b.dataset.title || '',
      variant: b.dataset.variant || '',
      price: Number(b.dataset.price || 0),
      swatch: b.dataset.swatch || '',
      cuff: b.dataset.cuff || '',
      thumb: b.dataset.thumb || '',
    }));
    this.maxStockings = Number(this.mantel?.dataset.max || 8);
    /** @type {string[]} */
    this.stockings = [];
    this.renderedStockings = 0;

    this.restore();
    this.bind();
    this.renderMantel();
    this.update();
    this.checkCart();
    this.initQuickView();
    this.initThread();
  }

  /* ----- State ----- */

  /** @param {HTMLElement} card */
  cardKey(card) {
    return `${card.dataset.shelf}:${card.dataset.handle}`;
  }

  /**
   * @param {HTMLElement} card
   * @param {boolean} on
   */
  setCard(card, on) {
    card.classList.toggle('is-on', on);
    const button = /** @type {HTMLButtonElement | null} */ (card.querySelector('[data-hp-toggle]'));
    if (!button) return;
    button.setAttribute('aria-pressed', String(on));
    button.textContent = (on ? button.dataset.on : button.dataset.off) || '';
  }

  restore() {
    const params = new URLSearchParams(window.location.search);
    /** @type {{ on?: string[], st?: string[] } | null} */
    let saved = null;

    if (params.has('hp') || params.has('hs')) {
      const handles = (params.get('hp') || '').split(',').filter(Boolean);
      const on = [];
      for (const handle of handles) {
        const card = this.cards.find((c) => c.dataset.handle === handle && !c.hasAttribute('data-sold-out'));
        if (card) on.push(this.cardKey(card));
      }
      saved = { on, st: (params.get('hs') || '').split(',').filter(Boolean) };
    } else {
      try {
        saved = JSON.parse(localStorage.getItem(this.storageKey) || 'null');
      } catch (e) {
        saved = null;
      }
    }

    const fabricHandles = new Set(this.fabrics.map((f) => f.handle));

    if (saved) {
      const on = new Set(saved.on || []);
      for (const card of this.cards) this.setCard(card, on.has(this.cardKey(card)) && !card.hasAttribute('data-sold-out'));
      this.stockings = (saved.st || []).filter((h) => fabricHandles.has(h)).slice(0, this.maxStockings);
      return;
    }

    if (this.root.dataset.starter === 'true') {
      for (const shelf of this.shelves) {
        if (shelf.dataset.mode !== 'one') continue;
        const first = shelf.querySelector('[data-hp-card]:not([data-sold-out])');
        if (first instanceof HTMLElement) this.setCard(first, true);
      }
      const count = Math.min(Number(this.mantel?.dataset.start || 0), this.maxStockings);
      if (this.fabrics.length) {
        this.stockings = Array.from({ length: count }, (_, i) => this.fabrics[i % this.fabrics.length].handle);
      }
    }
  }

  save() {
    const on = this.cards.filter((c) => c.classList.contains('is-on')).map((c) => this.cardKey(c));
    try {
      localStorage.setItem(this.storageKey, JSON.stringify({ on, st: this.stockings }));
    } catch (e) {
      /* storage unavailable: the plan still works for this visit */
    }
  }

  /** @returns {PlanLine[]} */
  planLines() {
    /** @type {PlanLine[]} */
    const lines = [];
    for (const shelf of this.shelves) {
      for (const card of shelf.querySelectorAll('[data-hp-card].is-on')) {
        if (!(card instanceof HTMLElement)) continue;
        const { variant, price } = this.cardVariant(card);
        lines.push({
          key: this.cardKey(card),
          title: card.dataset.title || '',
          thumb: card.dataset.thumb || '',
          variant,
          price,
          qty: 1,
          note: shelf.dataset.note || '',
        });
      }
    }
    for (const fabric of this.fabrics) {
      const qty = this.stockings.filter((h) => h === fabric.handle).length;
      if (!qty) continue;
      lines.push({
        key: `mantel:${fabric.handle}`,
        title: fabric.title,
        thumb: fabric.thumb,
        variant: fabric.variant,
        price: fabric.price,
        qty,
        note: this.mantel?.dataset.note || '',
      });
    }
    return lines;
  }

  /** @param {HTMLElement} card */
  cardVariant(card) {
    const select = card.querySelector('[data-hp-variant]');
    if (select instanceof HTMLSelectElement) {
      const option = select.selectedOptions[0];
      return { variant: select.value, price: Number(option?.dataset.price || card.dataset.price || 0) };
    }
    return { variant: card.dataset.variant || '', price: Number(card.dataset.price || 0) };
  }

  /** @param {number} cents */
  money(cents) {
    try {
      return formatMoney(cents, this.moneyFormat, this.currency);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }

  /* ----- Events ----- */

  bind() {
    for (const card of this.cards) {
      card.querySelector('[data-hp-toggle]')?.addEventListener('click', () => {
        const turningOn = !card.classList.contains('is-on');
        if (card.dataset.mode === 'one' && turningOn) {
          const shelf = card.closest('[data-hp-shelf]');
          shelf?.querySelectorAll('[data-hp-card]').forEach((c) => c instanceof HTMLElement && this.setCard(c, false));
        }
        this.setCard(card, turningOn);
        this.update();
      });

      const quick = card.querySelector('[data-hp-cart]');
      if (quick instanceof HTMLButtonElement) quick.addEventListener('click', () => this.quickAdd(card, quick));

      const select = card.querySelector('[data-hp-variant]');
      if (select instanceof HTMLSelectElement) {
        select.addEventListener('change', () => {
          const price = card.querySelector('[data-hp-price]');
          if (price) price.textContent = this.money(this.cardVariant(card).price);
          this.update();
        });
      }
    }

    if (this.mantel) {
      // Each fabric has its own count: + adds one stocking in that fabric, − takes one away.
      this.fabricRows.forEach((row, i) => {
        const handle = this.fabrics[i].handle;
        row.querySelector('[data-hp-fabric-plus]')?.addEventListener('click', () => {
          if (this.stockings.length >= this.maxStockings) return;
          this.stockings.push(handle);
          this.renderMantel();
          this.update();
        });
        row.querySelector('[data-hp-fabric-minus]')?.addEventListener('click', () => {
          const at = this.stockings.lastIndexOf(handle);
          if (at < 0) return;
          this.stockings.splice(at, 1);
          this.renderedStockings = this.stockings.length;
          this.renderMantel();
          this.update();
        });
      });
    }

    this.root.querySelector('[data-hp-add-all]')?.addEventListener('click', () => this.addAll());
    this.root.querySelector('[data-hp-ribbon-add]')?.addEventListener('click', (event) => {
      if (event.currentTarget instanceof HTMLButtonElement) this.addAll(event.currentTarget);
    });
    this.root.querySelector('[data-hp-copy]')?.addEventListener('click', () => this.copyLink());

    this.root.querySelector('.hp-browse')?.addEventListener('click', (event) => {
      const href = event.currentTarget instanceof HTMLAnchorElement ? event.currentTarget.getAttribute('href') : '';
      if (!href || !href.startsWith('#')) return;
      let target = document.querySelector(href);
      if (!target) target = document.querySelector('[id^="shopify-section-"][id$="__main"]');
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth' });
    });

    // Fired by the cart drawer and by our own adds, so the plan bar follows the real cart.
    document.addEventListener(CartAddEvent.eventName, () => this.checkCart());

    this.ribbon = this.root.querySelector('[data-hp-ribbon]');
    this.planBlock = this.root.querySelector('#your-plan');
    window.addEventListener('scroll', () => this.onScroll(), { passive: true });
  }

  /* ----- Mantel ----- */

  renderMantel() {
    if (!this.mantel) return;
    const svg = this.mantel.querySelector('[data-hp-garland]');
    if (!svg) return;

    if (!svg.querySelector('defs')) {
      const defs = svgEl('defs', {}, svg);
      for (const fabric of this.fabrics) {
        const body = svgEl('pattern', { id: `hp-pb-${fabric.handle}`, patternUnits: 'userSpaceOnUse', width: 90, height: 170 }, defs);
        svgEl('image', { href: fabric.swatch, width: 90, height: 170, preserveAspectRatio: 'xMidYMid slice' }, body);
        const cuff = svgEl('pattern', { id: `hp-pc-${fabric.handle}`, patternUnits: 'userSpaceOnUse', width: 80, height: 50 }, defs);
        svgEl('image', { href: fabric.cuff, width: 80, height: 50, preserveAspectRatio: 'xMidYMax slice' }, cuff);
      }
      svgEl('path', { d: 'M20 26 Q450 74 880 26', fill: 'none', stroke: '#E4CDB3', 'stroke-width': 2.5, 'stroke-dasharray': '8 6', 'stroke-linecap': 'round' }, svg);
      svgEl('g', { 'data-hp-hangers': '' }, svg);
    }

    const layer = svg.querySelector('[data-hp-hangers]');
    if (!layer) return;
    layer.replaceChildren();

    const count = this.stockings.length;
    const scale = Math.min(1.15, 760 / (Math.max(count, 1) * 100));
    /** @param {number} t */
    const curve = (t) => [
      (1 - t) * (1 - t) * 20 + 2 * (1 - t) * t * 450 + t * t * 880,
      (1 - t) * (1 - t) * 26 + 2 * (1 - t) * t * 74 + t * t * 26,
    ];
    const bodyPath = 'M18 44 H76 V112 C76 142 58 162 32 162 C12 162 0 150 0 134 C0 118 12 110 26 106 Z';

    this.stockings.forEach((handle, i) => {
      const fabric = this.fabrics.find((f) => f.handle === handle);
      if (!fabric) return;
      const [x, y] = curve((i + 1) / (count + 1));
      const group = svgEl('g', { transform: `translate(${x - 58 * scale} ${y}) scale(${scale})` }, layer);
      const isNew = i >= this.renderedStockings;
      const stocking = svgEl('g', { class: `hp-stocking${isNew ? ' is-new' : ''}` }, group);
      svgEl('title', {}, stocking).textContent = fabric.short;
      if (isNew) stocking.setAttribute('style', `animation-delay:${(i - this.renderedStockings) * 0.08}s`);
      svgEl('path', { d: 'M58 0 V14', stroke: '#E4CDB3', 'stroke-width': 1.5 }, stocking);
      svgEl('circle', { cx: 58, cy: 0, r: 3, fill: '#F2B134' }, stocking);
      svgEl('path', { d: bodyPath, fill: `url(#hp-pb-${fabric.handle})` }, stocking);
      svgEl('path', { class: 'hp-stocking__outline', d: bodyPath, fill: 'none', stroke: '#E4CDB3', 'stroke-width': 1.6, 'stroke-dasharray': '4 4' }, stocking);
      svgEl('rect', { x: 10, y: 14, width: 72, height: 32, fill: `url(#hp-pc-${fabric.handle})` }, stocking);
      svgEl('rect', { x: 10, y: 14, width: 72, height: 32, fill: 'none', stroke: '#E4CDB3', 'stroke-width': 1.2 }, stocking);
    });
    this.renderedStockings = count;

    if (!count) {
      const empty = svgEl('text', { x: 450, y: 150, 'text-anchor': 'middle', class: 'hp-garland__empty' }, layer);
      empty.textContent = 'Your stockings will hang here';
    }

    const full = count >= this.maxStockings;
    this.fabricRows.forEach((row, i) => {
      const n = this.stockings.filter((h) => h === this.fabrics[i].handle).length;
      const output = row.querySelector('[data-hp-fabric-count]');
      if (output) output.textContent = String(n);
      row.classList.toggle('is-on', n > 0);
      const badge = row.querySelector('[data-hp-fabric-badge]');
      if (badge) badge.textContent = n ? `× ${n}` : '';
      const minus = row.querySelector('[data-hp-fabric-minus]');
      if (minus instanceof HTMLButtonElement) minus.disabled = n === 0;
      const plus = row.querySelector('[data-hp-fabric-plus]');
      if (plus instanceof HTMLButtonElement) plus.disabled = full;
    });

    const total = this.mantel.querySelector('[data-hp-mantel-total]');
    if (total) {
      total.textContent = !count
        ? 'No stockings yet. Tap + next to a fabric to add one.'
        : `${count} stocking${count === 1 ? '' : 's'} on your mantel${full ? ` (the most we can hang is ${this.maxStockings})` : ''}.`;
    }
  }

  /* ----- Plan tag ----- */

  update() {
    const lines = this.planLines();
    const pieces = lines.reduce((sum, l) => sum + l.qty, 0);
    const total = lines.reduce((sum, l) => sum + l.qty * l.price, 0);

    const list = this.root.querySelector('[data-hp-lines]');
    if (list) {
      list.replaceChildren();
      if (!lines.length) {
        const li = document.createElement('li');
        li.className = 'hp-lines__empty';
        li.textContent = 'Your tag is empty. Choose a piece above to start.';
        list.appendChild(li);
      }
      for (const line of lines) {
        const li = document.createElement('li');
        const img = document.createElement('img');
        img.src = line.thumb;
        img.alt = '';
        img.width = 40;
        img.height = 40;
        img.loading = 'lazy';
        const text = document.createElement('span');
        text.className = 'hp-lines__t';
        text.textContent = line.qty > 1 ? `${line.title} × ${line.qty}` : line.title;
        if (line.note) {
          const small = document.createElement('small');
          small.textContent = line.note;
          text.appendChild(small);
        }
        const price = document.createElement('span');
        price.className = 'hp-lines__p';
        price.textContent = this.money(line.qty * line.price);
        li.append(img, text, price);
        list.appendChild(li);
      }
    }

    const totalEl = this.root.querySelector('[data-hp-total]');
    if (totalEl) totalEl.textContent = this.money(total);

    const shipText = this.root.querySelector('[data-hp-ship-text]');
    const shipBar = /** @type {HTMLElement | null} */ (this.root.querySelector('[data-hp-ship-bar]'));
    if (shipText && this.shipThreshold > 0) {
      const left = this.shipThreshold - total;
      shipText.textContent =
        total === 0
          ? `Free shipping on orders over ${this.money(this.shipThreshold)}.`
          : left > 0
            ? `${this.money(left)} away from free shipping.`
            : 'Your plan ships free.';
      if (shipBar) shipBar.style.width = `${Math.min(100, (total / this.shipThreshold) * 100)}%`;
    }

    const ribbonText = this.root.querySelector('[data-hp-ribbon-text]');
    if (ribbonText) ribbonText.textContent = `Your plan · ${pieces} piece${pieces === 1 ? '' : 's'} · ${this.money(total)}`;
    // Any change to the plan means the cart no longer matches it.
    this.setInCart(false);

    this.pieces = pieces;
    this.save();
    this.onScroll();
    this.syncQuickView();
  }

  /** @param {string} text */
  message(text) {
    const el = this.root.querySelector('[data-hp-message]');
    if (el) el.textContent = text;
  }

  /** @param {boolean} inCart */
  setInCart(inCart) {
    const status = this.root.querySelector('[data-hp-ribbon-status]');
    if (status) status.textContent = inCart ? 'In your cart ✓' : 'Not in your cart yet';
    const add = this.root.querySelector('[data-hp-ribbon-add]');
    if (add instanceof HTMLElement) add.hidden = inCart;
  }

  /** @param {HTMLButtonElement | null} [trigger] the button pressed, if not the one on the tag */
  async addAll(trigger = null) {
    const lines = this.planLines();
    const button = trigger || /** @type {HTMLButtonElement | null} */ (this.root.querySelector('[data-hp-add-all]'));
    if (!lines.length) {
      this.message('Choose a piece first.');
      return;
    }
    if (!button || button.getAttribute('aria-busy') === 'true') return;

    const label = button.textContent;
    button.setAttribute('aria-busy', 'true');
    button.textContent = trigger ? 'Adding…' : 'Adding your plan…';
    this.message('');

    const { added, problems } = await this.addToCart(lines);

    button.removeAttribute('aria-busy');
    button.textContent = label;
    if (added > 0 && !problems.length) this.setInCart(true);
    if (problems.length && added > 0) this.message(`Everything else is in your cart. ${problems.join(' ')}`);
    else if (problems.length) this.message(problems.join(' '));
    // Problems are explained on the tag, so bring it into view when the bar was used.
    if (problems.length && trigger) this.planBlock?.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth' });
  }

  /** Shows the plan as in the cart only while the cart holds everything in it (on load and after any cart change). */
  async checkCart() {
    if (!this.planLines().length) return;
    try {
      const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((r) => r.json());
      /** @type {Record<string, number>} */
      const have = {};
      for (const item of cart.items || []) have[item.variant_id] = (have[item.variant_id] || 0) + item.quantity;
      // Read the plan after the fetch so a change made meanwhile isn't judged against the old plan.
      /** @type {Record<string, number>} */
      const need = {};
      for (const line of this.planLines()) need[line.variant] = (need[line.variant] || 0) + line.qty;
      const keys = Object.keys(need);
      this.setInCart(keys.length > 0 && keys.every((v) => (have[v] || 0) >= need[v]));
    } catch (e) {
      /* leave the bar as it is */
    }
  }

  /**
   * Adds one card's piece straight to the cart, without touching the plan.
   * @param {HTMLElement} card
   * @param {HTMLButtonElement} button
   * @param {Element | null} [msg] where to explain a failed add (defaults to the card's own message)
   */
  async quickAdd(card, button, msg = card.querySelector('[data-hp-card-msg]')) {
    if (button.getAttribute('aria-busy') === 'true') return;
    const label = (button.dataset.label ||= button.textContent || '');
    button.setAttribute('aria-busy', 'true');
    button.textContent = 'Adding…';
    if (msg) msg.textContent = '';

    const { variant, price } = this.cardVariant(card);
    const { added, problems } = await this.addToCart([
      { key: this.cardKey(card), title: card.dataset.title || '', thumb: '', variant, price, qty: 1, note: '' },
    ]);

    button.removeAttribute('aria-busy');
    if (added > 0) {
      button.textContent = 'Added to cart ✓';
      setTimeout(() => (button.textContent = label), 2500);
    } else {
      button.textContent = label;
      if (msg) msg.textContent = problems.join(' ');
    }
  }

  /**
   * One request per line: each add either succeeds or fails on its own, so a
   * sold-out piece never blocks or duplicates the rest.
   * @param {PlanLine[]} lines
   * @returns {Promise<{ added: number, problems: string[] }>}
   */
  async addToCart(lines) {
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean)
      .join(',');
    // @ts-ignore Theme is a global set in snippets/scripts.liquid
    const url = window.Theme?.routes?.cart_add_url || '/cart/add.js';

    let added = 0;
    /** @type {string[]} */
    const problems = [];
    let sections;

    for (const line of lines) {
      /** @type {{ id: number, quantity: number, properties?: Record<string, string> }} */
      const item = { id: Number(line.variant), quantity: line.qty };
      if (this.planTag) item.properties = { _plan: this.planTag };
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [item], sections: sectionIds }),
        });
        const data = await response.json();
        if (data.status) {
          problems.push(`${line.title}: ${data.description || data.message}`);
        } else {
          added += line.qty;
          sections = data.sections;
        }
      } catch (error) {
        problems.push(`${line.title}: could not be added. Please try again.`);
      }
    }

    let itemCount = added;
    try {
      const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((r) => r.json());
      itemCount = cart.item_count;
    } catch (e) {
      /* fall back to the count we added */
    }

    if (added > 0) {
      this.root.dispatchEvent(
        new CartAddEvent({}, this.root.id, { source: 'holiday-planner', itemCount, sections })
      );
      // @ts-ignore Shopify analytics is provided by the storefront
      window.Shopify?.analytics?.publish?.('holiday_plan_added_to_cart', {
        pieces: added,
        lines: lines.map((l) => ({ variantId: l.variant, quantity: l.qty })),
      });
    }
    return { added, problems };
  }

  /* ----- Quick view ----- */

  initQuickView() {
    const dialog = this.root.querySelector('[data-hp-qv]');
    if (!(dialog instanceof HTMLDialogElement)) return;
    // Move the dialog to the end of <body>, so nothing it was nested in (overflow clipping, stacking
    // or containment on the page, which mobile Safari can apply even to modal dialogs) can hide it.
    document.querySelectorAll(`body > [data-hp-qv][data-section-id="${CSS.escape(dialog.dataset.sectionId || '')}"]`)
      .forEach((old) => old !== dialog && old.remove());
    document.body.appendChild(dialog);
    this.qv = dialog;
    /** @type {HTMLElement | null} the card or stocking the quick view is showing */
    this.qvItem = null;
    /** @type {Map<string, Promise<any>>} */
    this.productCache = new Map();

    // A card's photo or name opens the quick view. Modified clicks still open the product page.
    this.root.addEventListener('click', (event) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
      const link = event.target instanceof Element ? event.target.closest('.hp-card__media, .hp-card__name') : null;
      const item = link?.closest('[data-hp-card], [data-hp-fabric]');
      if (!(link instanceof HTMLElement) || !(item instanceof HTMLElement) || dialog.contains(link)) return;
      event.preventDefault();
      this.openQuickView(item, link);
    });

    dialog.querySelector('[data-hp-qv-close]')?.addEventListener('click', () => dialog.close());
    // A click on the dimmed backdrop lands on the dialog itself.
    dialog.addEventListener('click', (event) => event.target === dialog && dialog.close());
    dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('hp-qv-open');
      this.qvItem = null;
      // Closed with ×, Esc or the backdrop: drop the history entry opening it added.
      if (!this.qvLeaving && history.state?.hpQuickView) history.back();
      this.qvReturn?.focus({ preventScroll: true });
    });

    // The back button (or swipe back) closes the quick view instead of leaving the planner.
    // The cart drawer adds its own entry on top, so only close once ours is gone.
    if (history.state?.hpQuickView) history.replaceState({ ...history.state, hpQuickView: undefined }, '');
    window.addEventListener('popstate', () => {
      if (dialog.open && !history.state?.hpQuickView) dialog.close();
    });
  }

  /**
   * If the quick view isn't actually on screen (something on the page hid or moved it),
   * close it and open the product page instead, so shoppers are never left on a dimmed, locked page.
   * @param {HTMLElement} trigger
   */
  ensureQuickViewShows(trigger) {
    const dialog = this.qv;
    const href = trigger instanceof HTMLAnchorElement ? trigger.href : '';
    if (!dialog) return;
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        if (!dialog.open) return;
        const box = dialog.getBoundingClientRect();
        const style = getComputedStyle(dialog);
        const x = box.left + box.width / 2;
        const y = box.top + Math.min(box.height / 2, 60);
        const onTop = dialog.contains(document.elementFromPoint(x, y));
        const inView = box.width > 80 && box.height > 80 && box.bottom > 0 && box.top < window.innerHeight;
        if (onTop && inView && Number(style.opacity) > 0.5 && style.visibility !== 'hidden') return;
        console.warn('Holiday planner: quick view did not appear, opening the product page instead.', {
          box, opacity: style.opacity, visibility: style.visibility, onTop,
        });
        // Swap the quick view's history entry for the product page, so back returns to the planner.
        this.qvLeaving = true;
        dialog.close();
        if (href) window.location.replace(href);
        else if (history.state?.hpQuickView) history.back();
      })
    );
  }

  /** @param {string} handle */
  fetchProduct(handle) {
    let request = this.productCache.get(handle);
    if (!request) {
      request = fetch(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(handle)}.js`).then((r) => {
        if (!r.ok) throw new Error(`Product request failed: ${r.status}`);
        return r.json();
      });
      request.catch(() => this.productCache.delete(handle));
      this.productCache.set(handle, request);
    }
    return request;
  }

  /**
   * @param {HTMLElement} item
   * @param {HTMLElement} trigger
   */
  async openQuickView(item, trigger) {
    const dialog = this.qv;
    const body = dialog?.querySelector('[data-hp-qv-body]');
    const handle = item.dataset.handle;
    if (!dialog || !body || !handle) return;

    this.qvItem = item;
    // Return focus to the card's name link (the photo link is skipped by keyboard users).
    const name = item.querySelector('.hp-card__name');
    this.qvReturn = name instanceof HTMLElement ? name : trigger;
    const titleId = dialog.getAttribute('aria-labelledby') || '';
    body.innerHTML = `<p class="hp-qv__loading"><span class="hp-qv__title" id="${esc(titleId)}">${esc(item.dataset.title)}</span>Loading…</p>`;
    if (!dialog.open) {
      dialog.showModal();
      if (!history.state?.hpQuickView) history.pushState({ ...history.state, hpQuickView: true }, '');
    }
    this.qvLeaving = false;
    document.documentElement.classList.add('hp-qv-open');
    this.ensureQuickViewShows(trigger);

    try {
      const product = await this.fetchProduct(handle);
      if (this.qvItem === item) this.renderQuickView(item, product);
    } catch (error) {
      if (this.qvItem !== item) return;
      const href = trigger instanceof HTMLAnchorElement ? trigger.href : '';
      body.innerHTML = `<p class="hp-qv__loading"><span class="hp-qv__title" id="${esc(titleId)}">${esc(item.dataset.title)}</span>
        We couldn't load this piece. ${href ? `<a href="${esc(href)}">Open its page instead</a>.` : ''}</p>`;
    }
  }

  /**
   * @param {HTMLElement} item
   * @param {any} product the /products/{handle}.js response
   */
  renderQuickView(item, product) {
    const dialog = this.qv;
    const body = dialog?.querySelector('[data-hp-qv-body]');
    if (!dialog || !body) return;
    const titleId = dialog.getAttribute('aria-labelledby') || '';

    /** @type {{ src: string, alt: string }[]} */
    const images = (product.media || [])
      .map((/** @type {any} */ m) => ({ src: m.media_type === 'image' ? m.src : m.preview_image?.src, alt: m.alt || '' }))
      .filter((/** @type {{ src?: string }} */ m) => m.src);
    if (!images.length) for (const src of product.images || []) images.push({ src, alt: '' });

    const slides = images
      .map(
        (img, i) => `<figure class="hp-qv__slide" data-hp-qv-slide="${i}">
          <img src="${esc(sized(img.src, 900))}" srcset="${[480, 720, 900, 1200].map((w) => `${esc(sized(img.src, w))} ${w}w`).join(', ')}"
            sizes="(min-width: 860px) 520px, 100vw" alt="${esc(img.alt || `${product.title}, photo ${i + 1} of ${images.length}`)}"
            ${i ? 'loading="lazy"' : ''}>
        </figure>`
      )
      .join('');
    const thumbs =
      images.length > 1
        ? `<div class="hp-qv__thumbs">${images
            .map(
              (img, i) => `<button type="button" class="hp-qv__thumb" data-hp-qv-thumb="${i}" aria-label="Show photo ${i + 1} of ${images.length}"${i ? '' : ' aria-current="true"'}>
                <img src="${esc(sized(img.src, 160))}" alt="" loading="lazy"></button>`
            )
            .join('')}</div>`
        : '';

    const isCard = item.hasAttribute('data-hp-card');
    const cardSelect = isCard ? item.querySelector('[data-hp-variant]') : null;
    const select =
      cardSelect instanceof HTMLSelectElement
        ? `<label class="hp-qv__option"><span>${esc(product.options?.[0]?.name || 'Option')}</span>
            <select data-hp-qv-variant>${[...cardSelect.options]
              .map((o) => `<option value="${esc(o.value)}"${o.disabled ? ' disabled' : ''}${o.selected ? ' selected' : ''}>${esc(o.textContent?.trim())}</option>`)
              .join('')}</select></label>`
        : '';

    const tag = '<span class="hp-qv__tag" data-hp-qv-tag hidden>In your plan</span>';
    const actions = isCard
      ? `<button type="button" class="hp-add hp-qv__primary" data-hp-qv-toggle></button>${tag}
         ${item.querySelector('[data-hp-cart]') ? '<button type="button" class="hp-quick" data-hp-qv-cart>Or add to cart now</button>' : ''}`
      : `<p class="hp-label">On your mantel</p>
         <span class="hp-stepper" role="group" aria-label="${esc(item.dataset.short)} stockings">
           <button type="button" data-hp-qv-minus aria-label="One fewer ${esc(item.dataset.short)} stocking">−</button>
           <output data-hp-qv-count aria-live="polite">0</output>
           <button type="button" data-hp-qv-plus aria-label="One more ${esc(item.dataset.short)} stocking">+</button>
         </span>${tag}`;

    // Where this piece sits in the plan: the corner's eyebrow plus the step or row it is in.
    const corner = item.closest('.hp-corner');
    const where = [
      corner?.querySelector('.hp-corner__head--main .hp-eyebrow')?.textContent,
      item.closest('.hp-group')?.querySelector('.hp-group__head .hp-h2')?.textContent ||
        item.closest('[data-hp-shelf]')?.querySelector(':scope > .hp-label')?.textContent,
    ]
      .map((t) => (t || '').trim().replace(/\.$/, ''))
      .filter(Boolean);
    const tone = ['night', 'sand', 'linen'].find((t) => corner?.classList.contains(`hp-tone-${t}`)) || 'linen';
    dialog.dataset.tone = tone;

    body.innerHTML = `
      <div class="hp-qv__gallery">
        <div class="hp-qv__track" data-hp-qv-track tabindex="0" aria-label="Photos">${slides}</div>
        ${thumbs}
        <span class="hp-qv__knot" aria-hidden="true"></span>
      </div>
      <div class="hp-qv__info">
        ${where.length ? `<p class="hp-eyebrow hp-qv__where">${where.map(esc).join(' · ')}</p>` : ''}
        <h2 class="hp-qv__title" id="${esc(titleId)}">${esc(product.title)}</h2>
        <p class="hp-qv__price" data-hp-qv-price></p>
        ${select}
        <div class="hp-qv__actions">${actions}</div>
        <p class="hp-card__msg" data-hp-qv-msg role="status"></p>
        <div class="hp-qv__desc rte">${product.description || ''}</div>
        <a class="hp-qv__more" href="${esc(product.url)}">See the full product page</a>
      </div>`;

    const track = body.querySelector('[data-hp-qv-track]');
    const thumbButtons = [...body.querySelectorAll('[data-hp-qv-thumb]')];
    thumbButtons.forEach((button, i) =>
      button.addEventListener('click', () => {
        const slide = body.querySelector(`[data-hp-qv-slide="${i}"]`);
        if (track && slide instanceof HTMLElement) track.scrollTo({ left: slide.offsetLeft, behavior: reduceMotion() ? 'auto' : 'smooth' });
      })
    );
    track?.addEventListener(
      'scroll',
      () => {
        if (!(track instanceof HTMLElement)) return;
        const at = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
        thumbButtons.forEach((b, i) => b.toggleAttribute('aria-current', i === at));
      },
      { passive: true }
    );

    const variant = body.querySelector('[data-hp-qv-variant]');
    if (variant instanceof HTMLSelectElement && cardSelect instanceof HTMLSelectElement) {
      variant.addEventListener('change', () => {
        cardSelect.value = variant.value;
        cardSelect.dispatchEvent(new Event('change'));
      });
    }
    body.querySelector('[data-hp-qv-toggle]')?.addEventListener('click', () => {
      /** @type {HTMLButtonElement | null} */ (item.querySelector('[data-hp-toggle]'))?.click();
    });
    const cart = body.querySelector('[data-hp-qv-cart]');
    if (cart instanceof HTMLButtonElement) cart.addEventListener('click', () => this.quickAdd(item, cart, body.querySelector('[data-hp-qv-msg]')));
    body.querySelector('[data-hp-qv-plus]')?.addEventListener('click', () => {
      /** @type {HTMLButtonElement | null} */ (item.querySelector('[data-hp-fabric-plus]'))?.click();
    });
    body.querySelector('[data-hp-qv-minus]')?.addEventListener('click', () => {
      /** @type {HTMLButtonElement | null} */ (item.querySelector('[data-hp-fabric-minus]'))?.click();
    });

    this.syncQuickView();
  }

  /** Mirrors the open item's plan state, price and buttons into the quick view. */
  syncQuickView() {
    const item = this.qvItem;
    const body = this.qv?.querySelector('[data-hp-qv-body]');
    if (!item || !body) return;

    const price = body.querySelector('[data-hp-qv-price]');
    if (price) {
      const cents = item.hasAttribute('data-hp-card') ? this.cardVariant(item).price : Number(item.dataset.price || 0);
      price.textContent = `${this.money(cents)}${item.hasAttribute('data-hp-fabric') ? ' each' : ''}`;
    }

    const toggle = body.querySelector('[data-hp-qv-toggle]');
    const source = item.querySelector('[data-hp-toggle]');
    if (toggle instanceof HTMLButtonElement) {
      const on = item.classList.contains('is-on');
      toggle.disabled = !(source instanceof HTMLButtonElement) || source.disabled;
      toggle.setAttribute('aria-pressed', String(on));
      toggle.classList.toggle('is-on', on);
      // The tag beside it shows the status, so once chosen the button offers the way back out.
      toggle.textContent = on ? 'Remove from plan' : source instanceof HTMLButtonElement ? source.textContent : 'Sold out';
    }

    const count = body.querySelector('[data-hp-qv-count]');
    if (count) count.textContent = item.querySelector('[data-hp-fabric-count]')?.textContent || '0';
    const tag = body.querySelector('[data-hp-qv-tag]');
    if (tag instanceof HTMLElement) tag.hidden = !item.classList.contains('is-on');
    for (const [from, to] of [['[data-hp-fabric-minus]', '[data-hp-qv-minus]'], ['[data-hp-fabric-plus]', '[data-hp-qv-plus]']]) {
      const a = item.querySelector(from);
      const b = body.querySelector(to);
      if (a instanceof HTMLButtonElement && b instanceof HTMLButtonElement) b.disabled = a.disabled;
    }
  }

  async copyLink() {
    const params = new URLSearchParams();
    const on = this.cards.filter((c) => c.classList.contains('is-on')).map((c) => c.dataset.handle);
    if (on.length) params.set('hp', [...new Set(on)].join(','));
    if (this.stockings.length) params.set('hs', this.stockings.join(','));
    params.set('utm_source', 'plan_share');
    const link = `${window.location.origin}${window.location.pathname}?${params.toString()}`;
    try {
      await navigator.clipboard.writeText(link);
      this.message('Plan link copied. Anyone who opens it sees your plan.');
    } catch (e) {
      this.message(link);
    }
  }

  /* ----- Thread and plan bar ----- */

  initThread() {
    this.thread = /** @type {SVGSVGElement | null} */ (this.root.querySelector('[data-hp-thread]'));
    if (!this.thread) return;
    let timer = 0;
    const rebuild = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => this.buildThread(), 120);
    };
    new ResizeObserver(rebuild).observe(this.root);
    this.buildThread();
  }

  buildThread() {
    const svg = this.thread;
    if (!svg) return;
    const width = this.root.clientWidth;
    const height = this.root.offsetHeight;
    svg.setAttribute('width', String(width));
    svg.setAttribute('height', String(height));
    svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
    svg.replaceChildren();

    const rootBox = this.root.getBoundingClientRect();
    const wrap = this.root.querySelector('.hp-wrap');
    let x0 = 16;
    if (wrap) {
      const box = wrap.getBoundingClientRect();
      const pad = parseFloat(getComputedStyle(wrap).paddingLeft) || 40;
      x0 = Math.max(14, box.left - rootBox.left + pad * 0.4);
    }
    const amp = width > 760 ? 9 : 4;
    /** @param {number} y */
    const xAt = (y) => x0 + Math.sin(y / 70) * amp;

    let d = `M${x0} 0`;
    for (let y = 40; y <= height; y += 40) d += ` L${xAt(y).toFixed(1)} ${y}`;

    const defs = svgEl('defs', {}, svg);
    const id = this.root.dataset.sectionId || 'hp';
    const gradient = svgEl('linearGradient', { id: `hp-tg-${id}`, gradientUnits: 'userSpaceOnUse', x1: 0, y1: 0, x2: 0, y2: height }, defs);
    for (const corner of this.root.querySelectorAll('.hp-corner')) {
      if (!(corner instanceof HTMLElement)) continue;
      const color = corner.classList.contains('hp-tone-night') ? '#E4CDB3' : '#A32222';
      const top = corner.offsetTop / height;
      svgEl('stop', { offset: Math.min(1, top + 0.02), 'stop-color': color }, gradient);
      svgEl('stop', { offset: Math.min(1, (corner.offsetTop + corner.offsetHeight) / height - 0.01), 'stop-color': color }, gradient);
    }
    const mirror = svgEl('radialGradient', { id: `hp-tm-mir-${id}`, cx: '35%', cy: '35%', r: '70%' }, defs);
    [['0', '#fff'], ['.45', '#E9E4DA'], ['1', '#8E8A80']].forEach(([o, c]) => svgEl('stop', { offset: o, 'stop-color': c }, mirror));

    const mask = svgEl('mask', { id: `hp-tm-${id}`, maskUnits: 'userSpaceOnUse', x: 0, y: 0, width, height }, defs);
    this.threadMask = svgEl('path', {
      d, fill: 'none', stroke: '#fff', 'stroke-width': 30, pathLength: 1, 'stroke-dasharray': '1 1', 'stroke-dashoffset': 1,
    }, mask);
    svgEl('path', {
      d, fill: 'none', stroke: `url(#hp-tg-${id})`, 'stroke-width': 2.4, 'stroke-linecap': 'round', 'stroke-dasharray': '10 7', mask: `url(#hp-tm-${id})`,
    }, svg);

    for (const knot of this.root.querySelectorAll('[data-hp-knot]')) {
      if (!(knot instanceof HTMLElement) || !knot.offsetParent) continue; // skip hidden fallback headings
      const y = knot.getBoundingClientRect().top - rootBox.top + 8;
      const x = xAt(y);
      const g = svgEl('g', { mask: `url(#hp-tm-${id})` }, svg);
      svgEl('circle', { cx: x, cy: y, r: 9, fill: 'none', stroke: '#F2B134', 'stroke-width': 1.4, 'stroke-dasharray': '2 2' }, g);
      svgEl('circle', { cx: x, cy: y, r: 5.5, fill: `url(#hp-tm-mir-${id})` }, g);
    }
    this.onScroll();
  }

  onScroll() {
    const box = this.root.getBoundingClientRect();
    if (this.threadMask) {
      const progress = reduceMotion() ? 1 : Math.max(0, Math.min(1, (window.innerHeight * 0.7 - box.top) / box.height));
      this.threadMask.setAttribute('stroke-dashoffset', String(1 - progress));
    }
    if (this.ribbon && this.planBlock) {
      const nearPlan = this.planBlock.getBoundingClientRect().top < window.innerHeight * 0.85;
      const inPlanner = box.top < window.innerHeight * 0.4 && box.bottom > window.innerHeight;
      this.ribbon.classList.toggle('is-visible', inPlanner && !nearPlan && (this.pieces || 0) > 0);
    }
  }
}

/* ---------------------------------------------------------------- Boot */

function boot() {
  document.querySelectorAll('[data-hp-hero]').forEach((svg) => initHero(/** @type {SVGSVGElement} */ (svg)));
  document.querySelectorAll('[data-hp-planner]').forEach((root) => {
    if (!(root instanceof HTMLElement) || root.dataset.hpReady) return;
    root.dataset.hpReady = 'true';
    new HolidayPlanner(root);
  });
}

boot();
document.addEventListener('shopify:section:load', boot);
